import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Search, Download, ArrowLeft, AlertCircle } from 'lucide-react';
import {
  impactProductsService,
  type ImpactProduct,
  type ImpactProductDetail,
  type ImpactSortColumn,
} from '@/services/impactProductsService';
import { authFetch } from '@/utils/auth-fetch';

// State lifted into the parent page so it survives the page's loading cycle.
interface Props {
  siteId: string;
  dateFrom: string;        // computed by the page from preset/custom
  dateTo: string;          // computed by the page from preset/custom
  preset: string;          // '7'|'30'|'90'|'ytd'|'custom'
  onPresetChange: (v: string) => void;
  customFrom: string;
  customTo: string;
  onCustomChange: (from: string, to: string) => void;
}

// Shared tooltip props — keeps label text dark on Recharts' white box in class-based dark mode.
export const IMPACT_TOOLTIP_PROPS = {
  contentStyle: { background: '#fff', color: '#111827' },
  labelStyle: { color: '#111827' },
} as const;

const PRODUCT_DATE_RANGES = [
  { value: '7',      label: 'Last 7 days' },
  { value: '30',     label: 'Last 30 days' },
  { value: '90',     label: 'Last 90 days' },
  { value: 'ytd',    label: 'Since Jan 1' },
  { value: 'custom', label: 'Custom' },
];

const SORT_OPTIONS: { value: ImpactSortColumn; label: string }[] = [
  { value: 'llm_visits', label: 'LLM visits' },
  { value: 'seo_visits', label: 'SEO visits' },
  { value: 'llm_revenue', label: 'LLM revenue' },
  { value: 'seo_revenue', label: 'SEO revenue' },
];

function formatNum(n: number | string | null | undefined): string {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US');
}

function formatRevenue(n: number | string | null | undefined): string {
  if (n == null) return '—';
  return `€${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function daysAgo(days: number): string {
  return new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
}

// ── Ranked/search list ────────────────────────────────────────────────────────

// Exported for unit testing (debounce race, fake timers).
export function ProductList({
  siteId, dateFrom, dateTo, sort, onSelect,
}: {
  siteId: string;
  dateFrom: string;
  dateTo: string;
  sort: ImpactSortColumn;
  onSelect: (p: ImpactProduct) => void;
}) {
  const [data, setData] = useState<{ products: ImpactProduct[]; total: number; dataUntil: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const searchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Incremented before each request; checked after await to drop stale responses.
  const reqGenRef = useRef(0);

  const load = useCallback(async (q: string, off: number) => {
    const gen = ++reqGenRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await impactProductsService.list(siteId, {
        q: q || undefined,
        limit,
        offset: off,
        sort,
        from: dateFrom,
        to: dateTo,
      });
      if (gen !== reqGenRef.current) return; // stale — a newer request is in flight
      setData(result);
    } catch (err: any) {
      if (gen !== reqGenRef.current) return;
      setData(null); // clear stale rows — only controls + error + Retry should show
      setError(err.message);
    } finally {
      if (gen === reqGenRef.current) setLoading(false);
    }
  }, [siteId, sort, dateFrom, dateTo]);

  useEffect(() => {
    if (!dateFrom || !dateTo) return; // custom preset with no dates selected yet
    if (searchRef.current) clearTimeout(searchRef.current);
    setOffset(0);
    load(query, 0);
  }, [sort, dateFrom, dateTo, load]);

  // Clear pending debounce timer on unmount to avoid state updates after unmount.
  useEffect(() => () => { if (searchRef.current) clearTimeout(searchRef.current); }, []);

  const handleSearch = (q: string) => {
    setQuery(q);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => { setOffset(0); load(q, 0); }, 350);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search by product name or item ID (e.g. 1117974)"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="space-y-2">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button variant="outline" size="sm" onClick={() => load(query, offset)}>
            Retry
          </Button>
        </div>
      )}

      {!error && loading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : data ? (
        <>
          <div className="text-sm text-muted-foreground">
            {data.total.toLocaleString('en-US')} products
            {data.dataUntil && <span className="ml-2">· Data through {data.dataUntil}</span>}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">SEO visits</TableHead>
                <TableHead className="text-right">LLM visits</TableHead>
                <TableHead className="text-right">SEO revenue</TableHead>
                <TableHead className="text-right">LLM revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">No products found</TableCell>
                </TableRow>
              ) : data.products.map((p) => (
                <TableRow
                  key={p.item_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onSelect(p)}
                >
                  <TableCell>
                    <div className="font-medium text-sm">{p.productName || p.item_id}</div>
                    <div className="text-xs text-muted-foreground">{p.item_id}</div>
                  </TableCell>
                  <TableCell className="text-right">{formatNum(p.seo_visits)}</TableCell>
                  <TableCell className="text-right">{formatNum(p.llm_visits)}</TableCell>
                  <TableCell className="text-right">{formatRevenue(p.seo_revenue)}</TableCell>
                  <TableCell className="text-right">{formatRevenue(p.llm_revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data.total > limit && (
            <div className="flex items-center gap-2 justify-end">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => { const o = offset - limit; setOffset(o); load(query, o); }}>Previous</Button>
              <span className="text-sm text-muted-foreground">{offset + 1}–{Math.min(offset + limit, data.total)} of {data.total}</span>
              <Button variant="outline" size="sm" disabled={offset + limit >= data.total} onClick={() => { const o = offset + limit; setOffset(o); load(query, o); }}>Next</Button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

// ── Product detail ────────────────────────────────────────────────────────────

function ProductDetail({
  siteId, itemId, dateFrom, dateTo, onBack,
}: {
  siteId: string;
  itemId: string;
  dateFrom: string;
  dateTo: string;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<ImpactProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetry] = useState(0);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    impactProductsService.detail(siteId, itemId, { from: dateFrom, to: dateTo })
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [siteId, itemId, dateFrom, dateTo, retryCount]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-64 w-full" /><Skeleton className="h-48 w-full" /></div>;

  if (error) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />Back
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button variant="outline" size="sm" onClick={() => setRetry((c) => c + 1)}>
          Retry
        </Button>
      </div>
    );
  }

  if (!detail) return null;

  const { product, series, daily, dataUntil } = detail;

  const handleCsvDownload = async () => {
    setCsvDownloading(true);
    setCsvError(null);
    try {
      const url = impactProductsService.csvUrl(siteId, itemId, { from: dateFrom, to: dateTo });
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `impact-${itemId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      console.error('[ImpactProductTab] CSV download failed:', err.message);
      setCsvError(`Failed to download CSV: ${err.message}`);
    } finally {
      setCsvDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <div>
            <h3 className="font-semibold">{product.productName || product.itemId}</h3>
            <div className="text-xs text-muted-foreground">{product.itemId}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dataUntil && <span className="text-xs text-muted-foreground">Data through {dataUntil}</span>}
          <Button variant="outline" size="sm" onClick={handleCsvDownload} disabled={csvDownloading}>
            <Download className="h-4 w-4 mr-1" />{csvDownloading ? 'Downloading…' : 'CSV'}
          </Button>
        </div>
      </div>

      {csvError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{csvError}</AlertDescription>
        </Alert>
      )}

      {/* Chart: SEO vs LLM visits */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">SEO vs AI assistant visits (LLM)</CardTitle>
        </CardHeader>
        <CardContent>
          {series.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">No data for the selected period</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={series} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tickFormatter={(d: string) => d.slice(5)} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip {...IMPACT_TOOLTIP_PROPS} formatter={(v: number | string) => Number(v).toLocaleString('en-US')} />
                <Legend />
                <Line type="monotone" dataKey="seoVisits" name="SEO visits" stroke="#3b82f6" dot={false} />
                <Line type="monotone" dataKey="llmVisits" name="LLM visits" stroke="#22c55e" dot={false} />
                {product.firstSeen && (
                  <ReferenceLine x={product.firstSeen} stroke="#f59e0b" strokeDasharray="4 2" label={{ value: 'AEO', fontSize: 10 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Daily table: newest first */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Daily detail</CardTitle>
        </CardHeader>
        <CardContent>
          {daily.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">No data</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">SEO visits</TableHead>
                  <TableHead className="text-right">LLM visits</TableHead>
                  <TableHead className="text-right">Revenue SEO</TableHead>
                  <TableHead className="text-right">Revenue LLM</TableHead>
                  <TableHead className="text-right">SEO orders</TableHead>
                  <TableHead className="text-right">LLM orders</TableHead>
                  <TableHead className="text-right">Bot req.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {daily.map((row) => (
                  <TableRow key={row.day} className={row.isLastDay ? 'bg-amber-50' : undefined}>
                    <TableCell className="font-mono text-sm">{row.day}</TableCell>
                    <TableCell className="text-right">{formatNum(row.seoVisits)}</TableCell>
                    <TableCell className="text-right">{formatNum(row.llmVisits)}</TableCell>
                    <TableCell className="text-right">{formatRevenue(row.seoRevenue)}</TableCell>
                    <TableCell className="text-right">{formatRevenue(row.llmRevenue)}</TableCell>
                    <TableCell className="text-right">{formatNum(row.seoOrderLines)}</TableCell>
                    <TableCell className="text-right">{formatNum(row.llmOrderLines)}</TableCell>
                    <TableCell className="text-right">{formatNum(row.botRequests)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main tab component ────────────────────────────────────────────────────────

export default function ImpactProductTab({
  siteId, dateFrom, dateTo,
  preset, onPresetChange, customFrom, customTo, onCustomChange,
}: Props) {
  const [sort, setSort] = useState<ImpactSortColumn>('llm_visits');
  const [selectedProduct, setSelectedProduct] = useState<ImpactProduct | null>(null);
  const [customError, setCustomError] = useState<string | null>(null);

  const handleCustomFrom = (f: string) => {
    setCustomError(null);
    if (customTo && f > customTo) {
      setCustomError('From date must not be after To date');
    }
    onCustomChange(f, customTo);
  };

  const handleCustomTo = (t: string) => {
    setCustomError(null);
    if (customFrom && customFrom > t) {
      setCustomError('From date must not be after To date');
    }
    onCustomChange(customFrom, t);
  };

  return (
    <div className="space-y-4">
      {!selectedProduct && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <Select value={preset} onValueChange={onPresetChange}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_DATE_RANGES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={(v) => setSort(v as ImpactSortColumn)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Badge variant="secondary">By product</Badge>
          </div>
          {preset === 'custom' && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">From</label>
              <Input
                type="date"
                className="w-[150px]"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => handleCustomFrom(e.target.value)}
              />
              <label className="text-sm text-muted-foreground">To</label>
              <Input
                type="date"
                className="w-[150px]"
                value={customTo}
                min={customFrom || undefined}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => handleCustomTo(e.target.value)}
              />
              {customError && <span className="text-sm text-destructive">{customError}</span>}
            </div>
          )}
        </div>
      )}

      {selectedProduct ? (
        <ProductDetail
          siteId={siteId}
          itemId={selectedProduct.item_id}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onBack={() => setSelectedProduct(null)}
        />
      ) : preset === 'custom' && (!customFrom || !customTo) ? (
        <p className="text-sm text-muted-foreground py-4">Select start and end dates</p>
      ) : (
        <ProductList
          siteId={siteId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          sort={sort}
          onSelect={setSelectedProduct}
        />
      )}
    </div>
  );
}
